import { useCallback, useEffect, useRef, useState, RefObject } from 'react';
import { MasterDocument } from '@util/firestore/master';
import { ProductDocument } from '@util/types/firestore/products';
import { useAuth } from 'context/AuthContext';
import { viewMaster } from '@util/analytics';
import { useSectionVisibility } from '@util/analytics/masterTracking';
import { getMasterSpecRows } from '@util/master/extractKeySpecs';

import AnswerCard from './master/AnswerCard';
import HeroSection from './master/HeroSection';
import MasterSEO from './master/MasterSEO';
import StickyTabs from './master/StickyTabs';
import FeaturedListing from './master/FeaturedListing';
import AllListingsCarousel from './master/AllListingsCarousel';
import EmptyStateContainer from './master/EmptyStateContainer';
import OverviewKeyFeatures from './master/OverviewKeyFeatures';
import SpecificationsSection from './master/SpecificationsSection';
import ProductDescription from './master/ProductDescription';
import ReviewsSection from './master/ReviewsSection';
import VideosSection, { getVideosSplit } from './master/VideosSection';
import FAQSection from './master/FAQSection';
import ResourcesSection from './master/ResourcesSection';
import SimilarProducts from './master/SimilarProducts';
import AdminEditPanel from './master/AdminEditPanel';
import PricingGuideSection from './master/PricingGuideSection';
import shutterCountSlugs from '../data/shutter-count-slugs.json';

const SHUTTER_COUNT_SLUGS = new Set(shutterCountSlugs as string[]);

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  mirrorless_camera: 'Camera',
  dslr: 'Camera',
  medium_format: 'Camera',
  compact_camera: 'Camera',
  film_camera: 'Camera',
  cinema_camera: 'Camera',
  camcorder: 'Camcorder',
  action_camera: 'Camera',
  photo_lens: 'Lens',
  cinema_lens: 'Lens',
  drone: 'Drone',
  flash: 'Flash',
  tripod: 'Tripod',
  gimbal: 'Gimbal',
  monitor: 'Monitor',
  recorder: 'Recorder',
  microphone: 'Microphone',
  lighting: 'Lighting',
  accessory: 'Accessory',
};

/** Slim shape for similar master products — only display fields needed by SimilarProducts carousel */
export type SimilarMasterDisplay = Pick<
  MasterDocument,
  'id' | 'slug' | 'images' | 'thumbnail' | 'title' | 'brand' | 'price'
>;

/** Slim shape for listing data — only fields needed by FeaturedListing, AllListingsCarousel,
 *  SimilarListingsModule, MasterSEO, and AnswerCard on /m/ pages */
export type ListingDisplay = Pick<
  ProductDocument,
  | 'id' | 'title' | 'slug' | 'thumbnail' | 'images' | 'price' | 'og_price'
  | 'on_sale' | 'out_of_stock' | 'stock' | 'condition' | 'brand' | 'categories'
  | 'sku' | 'bumps' | 'favorite_count' | 'is_auction' | 'start_time' | 'end_time'
  | 'is_verified' | 'is_flat_rate' | 'seller_id' | 'shipping_costs' | 'gearMaster'
  | 'external_source' | 'integrations' | 'description' | 'account_id'
>;

export type MasterProductsPageV2Props = {
  master: MasterDocument;
  results: ListingDisplay[];
  similarMasters: SimilarMasterDisplay[];
  similarListings: ListingDisplay[];
};

export default function MasterProductsPageV2({
  master,
  results,
  similarMasters,
  similarListings,
}: MasterProductsPageV2Props) {
  const { userDoc } = useAuth();
  const sellUrl = userDoc?.account_activated ? '/sell/create-listing' : '/sell';
  const [hasPricingGuide, setHasPricingGuide] = useState(true);
  const handlePricingVisibility = useCallback((visible: boolean) => setHasPricingGuide(visible), []);

  // Track page view after 3s delay to filter out bots that bounce immediately.
  // Uses lastTrackedId (not boolean) so client-side navigation between /m/ pages
  // correctly tracks each master product, not just the first one.
  const lastTrackedId = useRef<string | null>(null);
  useEffect(() => {
    if (!master.id || lastTrackedId.current === master.id) return;
    const timer = setTimeout(() => {
      lastTrackedId.current = master.id;
      viewMaster(master).catch(() => {});
    }, 3000);
    return () => clearTimeout(timer);
  }, [master.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const typeLabel = PRODUCT_TYPE_LABELS[master.productType || ''] || '';
  const typeSuffix = typeLabel ? ` ${typeLabel}` : '';

  // Gate the specs section, heading, and StickyTabs tab on the SAME rows
  // SpecificationsSection renders (extractKeySpecs expands nested spec groups,
  // so a master with <3 top-level keys can still yield display rows). Deriving
  // this from the raw top-level key count would hide real, extractable spec
  // tables on partially-enriched masters.
  const hasSpecs = getMasterSpecRows(master).length > 0;

  // Mirror ProductDescription's own null check so the parent knows whether the
  // right-hand column will render anything.
  const hasDescription = !!(
    master.gmDescription ||
    (Array.isArray(master.gmPros) && master.gmPros.length > 0) ||
    (Array.isArray(master.gmCons) && master.gmCons.length > 0)
  );

  const { primaryVideo, additionalVideos } = getVideosSplit(master);
  const hasVideos = !!primaryVideo;
  const hasReviews = !!(master.reviews && master.reviews.length > 0);

  const hasManuals = !!(master.manuals && master.manuals.length > 0);
  const hasShutterArticle = SHUTTER_COUNT_SLUGS.has(master.slug);
  const shutterCountArticles = hasShutterArticle
    ? [{ title: `How to Check Shutter Count on ${master.brand} ${master.model}`, url: `https://gearfocus.com/blog/${master.slug}-shutter-count-2`, slug: `${master.slug}-shutter-count-2` }]
    : undefined;
  const hasResources = hasManuals || hasShutterArticle;

  // Section visibility tracking — fires master_section_view once per section per page load
  const masterTrackingParams = {
    id: master.id,
    slug: master.slug,
    brand: master.brand,
    model: master.model,
    productType: master.productType,
  };
  const { ref: overviewRef } = useSectionVisibility('overview', masterTrackingParams);
  const { ref: specificationsRef } = useSectionVisibility('specifications', masterTrackingParams);
  const { ref: pricingGuideRef } = useSectionVisibility('pricing-guide', masterTrackingParams);
  const { ref: reviewsRef } = useSectionVisibility('reviews', masterTrackingParams);
  const { ref: videosRef } = useSectionVisibility('videos', masterTrackingParams);
  const { ref: resourcesRef } = useSectionVisibility('resources', masterTrackingParams);
  const { ref: faqRef } = useSectionVisibility('faq', masterTrackingParams);

  return (
    <main role="main" aria-label={`${master.title} - Product Details`} className="w-full [overflow-x:clip]">
      {/* Skip navigation link */}
      <a href="#overview" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-[1rem] focus:bg-white focus:text-brand-secondary focus:underline">
        Skip to product overview
      </a>

      {/* SEO: meta tags + JSON-LD */}
      <MasterSEO master={master} listings={results} />

      {/* AnswerCard — owns the <h1>, first content block */}
      <AnswerCard master={master} listings={results} />

      {/* HeroSection — product image, title (no h1), breadcrumbs, CTA */}
      <HeroSection master={master} sellUrl={sellUrl} hasListings={results.length > 0} />

      {/* Sticky tab navigation */}
      <StickyTabs
        hasListings={results.length > 0}
        hasSimilar={similarMasters.length > 0}
        hasSpecifications={hasSpecs}
        hasReviews={hasReviews}
        hasFaq={!!(master.gmFaq && master.gmFaq.length > 0)}
        hasVideos={hasVideos}
        hasPricingGuide={hasPricingGuide}
        hasResources={hasResources}
      />

      {/* Listings area — featured + carousel, or empty state */}
      {results.length === 0 ? (
        <EmptyStateContainer
          master={master}
          sellUrl={sellUrl}
          similarListings={similarListings}
        />
      ) : (
        <>
          <div className="bg-gray-50">
            <FeaturedListing master={master} results={results} />
          </div>
          <AllListingsCarousel
            results={results}
            brand={master.brand}
            model={master.model}
          />
        </>
      )}

      {/* Overview + Key Features — gray background */}
      <div className="bg-[#f4f2f2]" ref={overviewRef as RefObject<HTMLDivElement>}>
        <OverviewKeyFeatures master={master} typeSuffix={typeSuffix} />
      </div>

      {/* Specifications + Description (two-column) — gray background.
          Render the section only when there is specs OR description content, and
          gate the Specifications heading on hasSpecs — so a master with <3 spec
          fields never shows an orphan "Specifications" heading over an empty
          column (StickyTabs already hides the Specifications tab in that case). */}
      {(hasSpecs || hasDescription) && (
        <div className="bg-[#f4f2f2]">
          <section
            id={hasSpecs ? 'specifications' : undefined}
            ref={hasSpecs ? (specificationsRef as RefObject<HTMLElement>) : undefined}
            aria-label={
              hasSpecs
                ? `${master.brand} ${master.model}${typeSuffix} Specifications`
                : `${master.brand} ${master.model} Description`
            }
            className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]"
          >
            <div className={`grid grid-cols-1 ${hasSpecs && hasDescription ? 'md:grid-cols-2 ' : ''}gap-[3rem] items-start`}>
              {hasSpecs && (
                <div>
                  <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[2rem]">
                    {typeLabel ? `${typeLabel} ` : ''}Specifications<span className="sr-only"> — {master.brand} {master.model}</span>
                  </h2>
                  <SpecificationsSection master={master} />
                </div>
              )}
              <ProductDescription master={master} />
            </div>
          </section>
        </div>
      )}

      {/* Pricing Guide — client-side data, hides when no data */}
      <div ref={pricingGuideRef as RefObject<HTMLDivElement>}>
        <PricingGuideSection
          master={master}
          sellUrl={sellUrl}
          onVisibilityChange={handlePricingVisibility}
        />
      </div>

      {/* Reviews + Videos — side-by-side on desktop, hidden when both empty */}
      {(hasReviews || hasVideos) && (
        <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[3rem]">
            {hasReviews && (
              <section id="reviews" ref={reviewsRef as RefObject<HTMLElement>} aria-label="Professional Reviews">
                <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[2rem]">
                  Reviews<span className="sr-only"> — {master.brand} {master.model}</span>
                </h2>
                <ReviewsSection master={master} additionalVideos={additionalVideos} />
              </section>
            )}

            {hasVideos && (
              <section id="videos" ref={videosRef as RefObject<HTMLElement>} aria-label="Videos">
                <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[2rem]">
                  Videos<span className="sr-only"> — {master.brand} {master.model}</span>
                </h2>
                <VideosSection master={master} />
              </section>
            )}
          </div>
        </div>
      )}

      {/* Resources & Downloads — white background */}
      {hasResources && (
        <div ref={resourcesRef as RefObject<HTMLDivElement>}>
          <ResourcesSection
            master={master}
            shutterCountArticles={shutterCountArticles}
          />
        </div>
      )}

      {/* FAQ — gray background */}
      <div className="bg-[#f4f2f2]" ref={faqRef as RefObject<HTMLDivElement>}>
        <FAQSection master={master} />
      </div>

      {/* Similar Products — gray background, last position */}
      <div className="bg-[#f4f2f2]">
        <SimilarProducts
          products={similarMasters}
          brand={master.brand}
          model={master.model}
        />
      </div>
      <AdminEditPanel master={master} />
    </main>
  );
}
